/**
 * LiveKit transport wiring for the live leg (SV-5-2) — MIRRORED from vCIP_droom_4.0/src/api/livekit.ts, not a
 * second client pattern: the same Room, the same RoomEvent → state mapping, the same rule that the wss URL and the
 * token are ALWAYS minted server-side (the join grant) and never hardcoded.
 *
 * Mapping (the droom's resume model, in the representative's vocabulary):
 *   Reconnecting                          → 'reconnecting'  (calm note, the call stays up)
 *   Reconnected                           → 'connected'
 *   Disconnected (client hang-up)         → 'left'
 *   Disconnected (ROOM_DELETED /
 *                 PARTICIPANT_REMOVED)    → 'ended'        (the officer finished — normal)
 *   Disconnected (DUPLICATE_IDENTITY)     → 'superseded'   (the leg was opened on another device)
 *   Disconnected (anything else)          → 'dropped'      (a network drop — the hook re-joins the SAME room)
 */

import { DisconnectReason, Room, RoomEvent, type RoomOptions } from 'livekit-client';
import type { LiveJoinGrant } from '@/api/capture';

export type LiveConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'dropped'
  | 'ended'
  | 'superseded'
  | 'left';

/** The slice of the LiveKit Room the hook and the tests depend on — a stub in tests, the real Room in the app. */
export interface LiveRoomLike {
  connect(url: string, token: string): Promise<void>;
  disconnect(): Promise<void> | void;
  readonly localParticipant: {
    setCameraEnabled(enabled: boolean): Promise<unknown>;
    setMicrophoneEnabled(enabled: boolean): Promise<unknown>;
    getTrackPublication(source: unknown): { videoTrack?: unknown; track?: unknown } | undefined;
  };
  readonly remoteParticipants: ReadonlyMap<string, { identity: string }>;
  on(event: string, listener: (...args: never[]) => void): unknown;
  off(event: string, listener: (...args: never[]) => void): unknown;
}

export interface LiveSession {
  readonly room: LiveRoomLike;
  connect(grant: LiveJoinGrant): Promise<void>;
  disconnect(): void;
}

/** Room factory seam: production builds a real LiveKit Room; component tests inject a stub (no real room in tests). */
export type RoomFactory = (options?: RoomOptions) => LiveRoomLike;

export const realRoomFactory: RoomFactory = (options) => new Room(options) as unknown as LiveRoomLike;

export function createLiveSession(
  onState: (state: LiveConnectionState) => void,
  factory: RoomFactory = realRoomFactory,
  roomOptions: RoomOptions = { adaptiveStream: true, dynacast: true },
): LiveSession {
  const room = factory(roomOptions);

  room.on(RoomEvent.Reconnecting, () => onState('reconnecting'));
  room.on(RoomEvent.Reconnected, () => onState('connected'));
  room.on(RoomEvent.Disconnected, ((reason?: DisconnectReason) => {
    if (reason === DisconnectReason.CLIENT_INITIATED) {
      onState('left');
    } else if (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.PARTICIPANT_REMOVED) {
      // The officer finished and the backend tore the room down (endRoom → deleteRoom) — the call is OVER,
      // and normally so (the droom's 2026-08-24 lesson: never tell a person whose leg just ended well that it lapsed).
      onState('ended');
    } else if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
      onState('superseded');
    } else {
      onState('dropped');
    }
  }) as (...args: never[]) => void);

  return {
    room,
    connect: async (grant) => {
      if (!grant.url || !grant.token) return; // nothing to connect to
      onState('connecting');
      await room.connect(grant.url, grant.token); // the URL RETURNED BY THE API — never a hardcoded host
      onState('connected');
    },
    disconnect: () => {
      void room.disconnect();
    },
  };
}
